import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Me } from '@/api/schemas/identity';
import type { Permission } from '@/auth/permissions';
import { accessTokenExpiresAt, createAuthAdapter, type AuthAdapter } from '@/auth/AuthAdapter';
import { setSessionRefresher } from '@/api/client';
import { AuthContext, type AuthState } from '@/auth/AuthContext';

/**
 * Session, roles, permissions and zone scope — W-0.7.
 *
 * `usePermission('bays.write')` is the only gate in the UI. Nothing in the
 * code ever checks a role name (§6.1): roles hold permissions, and a screen
 * that asks "is this user a supervisor" is a screen that breaks the first time
 * UDC edits a role.
 */
export function AuthProvider({
  children,
  adapter: injected,
}: {
  children: React.ReactNode;
  adapter?: AuthAdapter;
}) {
  const adapterRef = useRef<AuthAdapter>(injected ?? createAuthAdapter());
  const adapter = adapterRef.current;

  const [status, setStatus] = useState<AuthState['status']>('loading');
  const [booting, setBooting] = useState(true);
  const [me, setMe] = useState<Me | null>(null);
  const [error, setError] = useState<Error | null>(null);

  /**
   * `rethrow` is the difference between restoring a session and attempting
   * one. A failed restore is just "signed out" and the shell carries on; a
   * failed sign-in has to reach the form, because the form is where the
   * reason belongs — a 422 on a bad role assignment is a field error next to
   * the input, not a banner somewhere else on the page.
   */
  const load = useCallback(
    async (resolve: () => Promise<Me | null>, { rethrow = false } = {}): Promise<Me | null> => {
      setStatus('loading');
      setError(null);
      try {
        const next = await resolve();
        setMe(next);
        setStatus(next ? 'signed-in' : 'signed-out');
        return next;
      } catch (caught) {
        const failure = caught instanceof Error ? caught : new Error(String(caught));
        setError(failure);
        setMe(null);
        // A refused sign-in leaves the visitor signed out and looking at the
        // form; only a failed *restore* is the app-level error state.
        setStatus(rethrow ? 'signed-out' : 'error');
        if (rethrow) throw failure;
        return null;
      } finally {
        // However the first attempt ends, it has ended: the answer is known
        // and the application may draw itself.
        setBooting(false);
      }
    },
    [],
  );

  useEffect(() => {
    void load(() => adapter.restore());
  }, [adapter, load]);

  /*
   * Renewal, from both ends.
   *
   * The access token lasts an hour. The reactive half is registered on the
   * client: a 401 refreshes once and retries, so a request that happens to
   * straddle expiry succeeds instead of surfacing as an error state. The
   * proactive half is the timer below, which renews a minute early so the
   * common case never produces a 401 at all.
   *
   * Signing out clears the refresher, or a stale 401 from an in-flight poll
   * could revive a session the visitor just ended.
   */
  useEffect(() => {
    setSessionRefresher(() => adapter.refreshSession());
    return () => setSessionRefresher(null);
  }, [adapter]);

  useEffect(() => {
    if (status !== 'signed-in') return;
    const expiry = accessTokenExpiresAt();
    if (expiry === null) return;

    // A minute of headroom, and never a negative or absurd delay: a server
    // clock ahead of ours must not schedule a refresh storm.
    const delay = Math.max(5_000, expiry - Date.now() - 60_000);
    const timer = setTimeout(() => {
      void adapter.refreshSession().then((ok) => {
        // A refused refresh is the session ending. Say so, rather than
        // leaving the screens to discover it one failed poll at a time.
        if (!ok) setStatus('signed-out');
      });
    }, delay);
    return () => clearTimeout(timer);
  }, [adapter, status, me]);

  const value = useMemo<AuthState>(() => {
    const permissions = new Set<Permission>(me?.permissions ?? []);
    return {
      status,
      booting,
      me,
      error,
      adapter,
      signInWithSso: () => load(() => adapter.signInWithSso(), { rethrow: true }),
      signInWithLocal: (username, password) =>
        load(() => adapter.signInWithLocal(username, password), { rethrow: true }),
      signOut: async () => {
        await adapter.signOut();
        setMe(null);
        setStatus('signed-out');
      },
      refresh: async () => {
        await load(() => adapter.restore());
      },
      has: (permission) => permissions.has(permission),
      scopeLimited: me?.zoneScope.limited ?? false,
    };
  }, [adapter, booting, error, load, me, status]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
